/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

type FaultyIntentNotificationProps = {
    /**
     * The intent to alarm the user about if needed.
     */
    intentId: LuisId;

    /**
     * Indicates if the incorrect filter is already active.
     */
    isIncorrectFilterActive: boolean;

    /**
     * Indicates if the unclear filter is already active.
     */
    isUnclearFilterActive: boolean;

    /**
     * A callback fires when user chooses to apply the filters
     * that this component alarms him/her about.
     */
    onApplyFilters: (filters: PassedFilters['differenceFilters']) => void;
};

const messages = defineMessages({
    generalDescription: {
        id: 'intents.faultyIntentNotification.generalDescription',
        defaultMessage: 'This intent has a number of'
    },
    incorrectPredictions: {
        id: 'intents.faultyIntentNotification.incorrectPredictions',
        defaultMessage: 'incorrect predictions'
    },
    and: {
        id: 'intents.faultyIntentNotification.and',
        defaultMessage: 'and'
    },
    unclearPredictions: {
        id: 'intents.faultyIntentNotification.unclearPredictions',
        defaultMessage: 'unclear predictions'
    },
    applyFilters: {
        id: 'intents.faultyIntentNotification.applyFilters',
        defaultMessage: 'Apply filters.'
    }
});

const linkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { textDecoration: 'underline' } });

/**
 * Displays the top right notification in the intent page.
 * This notification alarms the user about being the current intent
 * one of the top three incorrect, unclear or both intents.
 */
export const FaultyIntentNotification = (props: FaultyIntentNotificationProps) => {
    const { intentId, isIncorrectFilterActive, isUnclearFilterActive, onApplyFilters } = props;
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;
    const isIncorrect = analyticsStore.incorrectIntents.slice(0, 3).some(intent => intent.intentId === intentId);
    const isUnclear = analyticsStore.unclearIntents.slice(0, 3).some(intent => intent.intentId === intentId);

    const filtersToApply: PassedFilters['differenceFilters'] = [];

    if (isIncorrect) {
        filtersToApply.push('incorrect');
    }

    if (isUnclear) {
        filtersToApply.push('unclear');
    }

    return (
        ((!isUnclearFilterActive && isUnclear) || (!isIncorrectFilterActive && isIncorrect)) && (
            <Stack grow={1} horizontal verticalAlign="center" horizontalAlign="end" tokens={{ childrenGap: 5 }}>
                <Icon iconName="Info" />

                <LuisText>
                    {localize(messages.generalDescription)}
                    &nbsp;
                    {isIncorrect && (
                        <LuisText>
                            {localize(messages.incorrectPredictions)}
                            &nbsp;
                        </LuisText>
                    )}
                    {isIncorrect && isUnclear && (
                        <LuisText>
                            {localize(messages.and)}
                            &nbsp;
                        </LuisText>
                    )}
                    {isUnclear && (
                        <LuisText>
                            {localize(messages.unclearPredictions)}
                            &nbsp;
                        </LuisText>
                    )}
                    <Link styles={linkStyles} onClick={() => onApplyFilters(filtersToApply)}>
                        {localize(messages.applyFilters)}
                    </Link>
                </LuisText>
            </Stack>
        )
    );
};
